import { useState, useEffect } from 'react';
import api from '../../api/axios';

const BHK_TYPES = ["1 BHK", "2 BHK", "3 BHK", "4 BHK", "Villa", "Office Space", "Other"];
const POSSESSION_OPTS = ["Immediately", "In a Month", "2-3 Month", "3-6 Month", "0.6-1 Year"];
const PROJECT_STAGE_OPTS = [
  "Client Won", "In Progress", "Site Visit Done", "Office Visit Done",
  "Quotation Sent", "Revised Quotation Sent", "Lead Received", "Call NR", "Client Loss"
];

export default function DashboardProjectModal({ isOpen, onClose, onSuccess, salespersons = [] }) {
  const [mode, setMode] = useState('new'); // 'new' | 'existing'
  const [existingProjects, setExistingProjects] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [selectedExistingId, setSelectedExistingId] = useState('');

  const [form, setForm] = useState({
    clientName: '',
    phone: '',
    email: '',
    estBudget: '',
    bhkType: '3 BHK',
    possessionMonth: 'In a Month',
    stage: 'Client Won',
    propertyAddress: '',
    city: 'Pune',
    state: 'Maharashtra',
    pincode: '',
    location: '',
    assignedTo: '',
    existingClientId: '',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && mode === 'existing' && existingProjects.length === 0) {
      setLoadingExisting(true);
      api.get('/projects', { params: { limit: 100 } })
        .then((res) => {
          const list = res.data?.data?.projects || res.data?.projects || [];
          setExistingProjects(list);
        })
        .catch(console.error)
        .finally(() => setLoadingExisting(false));
    }
  }, [isOpen, mode, existingProjects.length]);

  if (!isOpen) return null;

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setError('');
    if (newMode === 'new') {
      setSelectedExistingId('');
      setForm((prev) => ({
        ...prev,
        clientName: '',
        phone: '',
        email: '',
        propertyAddress: '',
        existingClientId: '',
      }));
    }
  };

  const handleSelectExisting = (e) => {
    const projId = e.target.value;
    setSelectedExistingId(projId);
    if (!projId) {
      setForm((prev) => ({
        ...prev,
        clientName: '',
        phone: '',
        email: '',
        propertyAddress: '',
        city: 'Pune',
        state: 'Maharashtra',
        existingClientId: '',
      }));
      return;
    }

    const found = existingProjects.find((p) => p._id === projId);
    if (found) {
      const clientId = typeof found.client === 'object' ? found.client?._id : found.client;
      setForm((prev) => ({
        ...prev,
        clientName: found.client?.name || found.clientName || '',
        phone: found.client?.phone || found.phone || '',
        email: found.client?.email || found.email || '',
        propertyAddress: found.propertyAddress || found.client?.propertyAddress || '',
        city: found.city || 'Pune',
        state: found.state || 'Maharashtra',
        bhkType: found.bhkType || prev.bhkType,
        estBudget: found.estBudget || prev.estBudget,
        possessionMonth: found.possessionMonth || prev.possessionMonth,
        stage: 'Client Won',
        existingClientId: clientId || '',
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.clientName.trim()) {
      setError('Client or project name is required.');
      return;
    }
    if (!form.phone.trim()) {
      setError('Phone number is required.');
      return;
    }
    const budget = parseFloat(form.estBudget);
    if (!budget || budget < 1) {
      setError('Estimated budget must be at least ₹1.');
      return;
    }
    if (!form.propertyAddress.trim()) {
      setError('Property address is required.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        clientName: form.clientName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        estBudget: budget,
        assignedTo: form.assignedTo || null,
        client: form.existingClientId || undefined,
      };

      const res = await api.post('/projects', payload);
      const createdProject = res.data?.data?.project || res.data?.project;
      onSuccess?.('Project created successfully!', createdProject);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create project.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal dash-action-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3 className="modal-title">🏗️ New Project</h3>
            <p style={{ margin: '3px 0 0 0', fontSize: '0.8rem', color: 'var(--muted)' }}>
              Initialize an active client project in the execution pipeline
            </p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        {/* Mode Selector */}
        <div style={{ display: 'flex', padding: '12px 1.75rem 0', gap: '8px' }}>
          <button
            type="button"
            className={`dash-pill-btn ${mode === 'new' ? 'active' : ''}`}
            onClick={() => handleModeChange('new')}
            style={{ fontSize: '0.8rem', padding: '5px 14px' }}
          >
            New Client Project
          </button>
          <button
            type="button"
            className={`dash-pill-btn ${mode === 'existing' ? 'active' : ''}`}
            onClick={() => handleModeChange('existing')}
            style={{ fontSize: '0.8rem', padding: '5px 14px' }}
          >
            Convert From Existing Lead / Client
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ maxHeight: 'calc(80vh - 150px)', overflowY: 'auto' }}>
            {error && (
              <div className="alert alert-error" style={{ marginBottom: '1rem', padding: '0.65rem 0.9rem', fontSize: '0.85rem' }}>
                {error}
              </div>
            )}

            {mode === 'existing' && (
              <div className="form-group" style={{ background: '#F8FAFC', padding: '12px', borderRadius: 8, border: '1px solid #E2E8F0' }}>
                <label className="form-label">Select Existing Lead / Client</label>
                <select
                  className="form-select"
                  value={selectedExistingId}
                  onChange={handleSelectExisting}
                  disabled={loadingExisting}
                >
                  <option value="">-- Choose lead or client to convert --</option>
                  {existingProjects.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.client?.name || p.clientName || 'Unnamed'} ({p.phone || 'No phone'}) · Stage: {p.stage || 'Lead'}
                    </option>
                  ))}
                </select>
                {loadingExisting && <p style={{ fontSize: '0.75rem', color: 'var(--muted)', margin: '4px 0 0 0' }}>Loading existing pipeline...</p>}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Client / Project Name *</label>
                <input
                  type="text"
                  name="clientName"
                  className="form-input"
                  placeholder="e.g. Vikram Patel"
                  value={form.clientName}
                  onChange={handleChange}
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-input"
                  placeholder="e.g. 9822001122"
                  value={form.phone}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  placeholder="client@email.com"
                  value={form.email}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Project Budget (₹) *</label>
                <input
                  type="number"
                  name="estBudget"
                  className="form-input"
                  placeholder="e.g. 1200000"
                  value={form.estBudget}
                  onChange={handleChange}
                  min="1"
                  step="1"
                  required
                />
              </div>
            </div>

            <div className="form-row-3">
              <div className="form-group">
                <label className="form-label">BHK / Space *</label>
                <select
                  name="bhkType"
                  className="form-select"
                  value={form.bhkType}
                  onChange={handleChange}
                  required
                >
                  {BHK_TYPES.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Project Stage *</label>
                <select
                  name="stage"
                  className="form-select"
                  value={form.stage}
                  onChange={handleChange}
                  required
                >
                  {PROJECT_STAGE_OPTS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Target Possession *</label>
                <select
                  name="possessionMonth"
                  className="form-select"
                  value={form.possessionMonth}
                  onChange={handleChange}
                  required
                >
                  {POSSESSION_OPTS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Project Manager / Sales Lead</label>
              <select
                name="assignedTo"
                className="form-select"
                value={form.assignedTo}
                onChange={handleChange}
              >
                <option value="">Unassigned</option>
                {salespersons.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name} {s.department ? `(${s.department})` : s.role ? `(${s.role})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Property Address *</label>
              <input
                type="text"
                name="propertyAddress"
                className="form-input"
                placeholder="e.g. Tower B, Flat 1204, Lodha Belmondo"
                value={form.propertyAddress}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row-3">
              <div className="form-group">
                <label className="form-label">City *</label>
                <input
                  type="text"
                  name="city"
                  className="form-input"
                  placeholder="City"
                  value={form.city}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">State *</label>
                <input
                  type="text"
                  name="state"
                  className="form-input"
                  placeholder="State"
                  value={form.state}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Pincode</label>
                <input
                  type="text"
                  name="pincode"
                  className="form-input"
                  placeholder="Pincode"
                  value={form.pincode}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Landmark / Site Details</label>
              <input
                type="text"
                name="location"
                className="form-input"
                placeholder="e.g. Near Mumbai-Pune Expressway"
                value={form.location}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
              style={{ background: '#976793' }}
            >
              {saving ? 'Creating Project...' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
